import {
  Box,
  Button,
  Container,
  createTheme,
  Stack,
  Theme,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useState } from 'react';
import { TourTypeURL } from '@/_models/Tour';
import ContactAndMap from './components/ContactAndMap';
import { Description } from './components/Description';
import FloorPlans from './components/FloorPlans';
import { Footer } from './components/Footer';
import Gallery from './components/gallery/Gallery';
import { Header } from './components/Header';
import IframeResponsive from './components/IframeResponsive';
import Navbar from './components/navbar/Navbar';
import { Summary } from './components/Summary';
import componentsOverride from './theme/overrides';
import typography from './theme/typography';
import color from '@/utils/muiColor';

function ModernPublicAsset({ whitelabel, tour }: any) {
  const [section, setSection] = useState<string>('3_d_tour');

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const iframeSrcPrefixByTourType = TourTypeURL[tour.mode];
  const hasFloorPlans = tour?.floorPlansEnabled && !!tour?.floorPlans?.length;

  const primaryColor = whitelabel?.primaryColor || '#2055ff';
  const secondaryColor = whitelabel?.secondaryColor || '#000000';

  return (
    <ThemeProvider
      theme={(theme: Theme) =>
        createTheme({
          ...theme,
          components: componentsOverride(),
          typography: {
            fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
            ...typography,
          },
          palette: {
            ...theme.palette,
            primary: {
              light: color.light(primaryColor),
              main: primaryColor,
              dark: color.dark(primaryColor),
            },
            secondary: {
              light: color.light(secondaryColor),
              main: secondaryColor,
              dark: color.dark(secondaryColor),
            },
          },
        })
      }
    >
      <Box
        className="no-reset"
        sx={{
          background: '#F8F8F8',
          minHeight: '100vh',
        }}
      >
        <Navbar
          whitelabel={whitelabel}
          setSection={setSection}
          isDesktop={isDesktop}
          hasFloorPlans={hasFloorPlans}
          translation={tour?.translation}
        />

        <Container sx={{ py: 2 }}>
          <Stack spacing={4}>
            <Header tour={tour} isDesktop={isDesktop} />
            <Description text={tour.assetDescription} tour={tour} />

            {hasFloorPlans && (
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  variant={section === '3_d_tour' ? 'contained' : 'outlined'}
                  size="large"
                  color="primary"
                  sx={{
                    color: section === '3_d_tour' ? '#fff' : 'gray',
                    borderColor: 'gray',
                    '&:hover': {
                      borderColor: 'gray',
                    },
                  }}
                  onClick={() => setSection('3_d_tour')}
                >
                  {tour?.translation?.threeDTour || '3D Tour'}
                </Button>
                <Button
                  variant={section === 'floor_plans' ? 'contained' : 'outlined'}
                  color="primary"
                  size="large"
                  sx={{
                    color: section === 'floor_plans' ? '#fff' : 'gray',
                    borderColor: 'gray',
                    '&:hover': {
                      borderColor: 'gray',
                    },
                  }}
                  onClick={() => setSection('floor_plans')}
                >
                  {tour?.translation?.floorPlans || 'Floor Plans'}
                </Button>
              </Stack>
            )}
            {!hasFloorPlans && (
              <Typography variant="h5" sx={{ marginBottom: '-15px !important' }}>
                {tour?.translation?.threeDTour || '3D Tour'}
              </Typography>
            )}
            {section === '3_d_tour' && (
              <IframeResponsive
                ratio={isDesktop ? '16/9' : '1/1'}
                previewSrc={`${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${tour.mainImage}`}
                src={`/${iframeSrcPrefixByTourType}/${tour.slug}?messenger=0`}
                whitelabel={whitelabel}
              />
            )}
            {section === 'floor_plans' && <FloorPlans tour={tour} isDesktop={isDesktop} />}
            <Gallery tour={tour} />
            {tour?.summary && <Summary text={tour.summary} tour={tour} />}
            <ContactAndMap tour={tour} isDesktop={isDesktop} />
          </Stack>
        </Container>
        <Footer whitelabel={whitelabel} />
      </Box>
    </ThemeProvider>
  );
}
export default ModernPublicAsset;
